import React, { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { authAPI } from '../api/api';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated, isAdmin, logout, user, setUser } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
  });
  const [updating, setUpdating] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path ? 'text-primary-600 font-bold' : 'text-gray-600 hover:text-primary-600';
  };

  const handleProfileClick = () => {
    setProfileData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
    });
    setShowProfileModal(true);
  };

  const handleProfileUpdate = async () => {
    try {
      setUpdating(true);
      const res = await authAPI.updateCurrentUser(profileData);
      setUser(res.data);
      setShowProfileModal(false);
      alert(language === 'ru' ? 'Профиль обновлен!' : 'Profil yangilandi!');
    } catch (err) {
      console.error('Profile update failed', err);
      alert(language === 'ru' ? 'Ошибка обновления' : 'Yangilashda xatolik');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Top Header */}
      <header className="bg-white/95 backdrop-blur-md shadow-xs sticky top-0 z-50 border-b border-gray-100 mt-14">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="text-xl sm:text-2xl font-black text-primary-600 tracking-tight flex items-center gap-1.5">
              <span>🏠</span> Samuyjoy
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className={`transition text-sm font-semibold ${isActive('/')}`}>
                {t('home')}
              </Link>
              <Link to="/search" className={`transition text-sm font-semibold ${isActive('/search')}`}>
                {t('search')}
              </Link>

              {isAuthenticated && (
                <Link to="/dashboard" className={`transition text-sm font-semibold ${isActive('/dashboard')}`}>
                  {t('myListings')}
                </Link>
              )}

              {isAdmin && (
                <Link to="/admin" className={`transition text-sm font-semibold ${isActive('/admin')}`}>
                  🛡️ {t('admin')}
                </Link>
              )}

              <LanguageSwitcher />

              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleProfileClick}
                    className="text-xs font-bold text-gray-700 bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition"
                  >
                    👤 {user?.first_name || user?.username}
                  </button>
                  <button onClick={logout} className="btn btn-secondary text-xs font-semibold py-1.5 px-3">
                    {t('logout')}
                  </button>
                </div>
              ) : (
                <Link to="/login" className="btn btn-primary text-xs font-bold py-2 px-4 shadow">
                  {t('login')}
                </Link>
              )}
            </div>

            {/* Mobile Top Header Actions */}
            <div className="md:hidden flex items-center space-x-2">
              <LanguageSwitcher />
              {isAuthenticated ? (
                <button
                  onClick={handleProfileClick}
                  className="w-8 h-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center font-bold text-xs border border-primary-200"
                >
                  {(user?.first_name || user?.username || 'U')[0].toUpperCase()}
                </button>
              ) : (
                <Link to="/login" className="text-xs font-bold text-primary-600 bg-primary-50 px-2.5 py-1 rounded-lg">
                  {t('login')}
                </Link>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content Area */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Mobile Bottom Navigation Bar (Always fixed at bottom on small screens) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 z-50 shadow-lg">
        <div className="grid grid-cols-4 gap-1 px-1 py-1.5 max-w-md mx-auto">
          <Link to="/" className={`flex flex-col items-center py-1 rounded-lg transition ${isActive('/')}`}>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
            <span className="text-[10px] font-semibold mt-0.5">{t('home')}</span>
          </Link>

          <Link to="/search" className={`flex flex-col items-center py-1 rounded-lg transition ${isActive('/search')}`}>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            <span className="text-[10px] font-semibold mt-0.5">{t('search')}</span>
          </Link>

          <Link to={isAuthenticated ? "/dashboard" : "/login"} className={`flex flex-col items-center py-1 rounded-lg transition ${isActive('/dashboard')}`}>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            <span className="text-[10px] font-semibold mt-0.5">{t('createListing')}</span>
          </Link>

          {isAdmin ? (
            <Link to="/admin" className={`flex flex-col items-center py-1 rounded-lg transition ${isActive('/admin')}`}>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              <span className="text-[10px] font-semibold mt-0.5">{t('admin')}</span>
            </Link>
          ) : (
            <Link to={isAuthenticated ? "/dashboard" : "/login"} className={`flex flex-col items-center py-1 rounded-lg transition ${isActive('/login')}`}>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              <span className="text-[10px] font-semibold mt-0.5">{isAuthenticated ? t('myListings') : t('login')}</span>
            </Link>
          )}
        </div>
      </div>

      {/* Desktop Footer */}
      <footer className="bg-gray-900 text-white py-10 mt-12 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <span>🏠</span> Samuyjoy
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                {t('footerDescription')}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-gray-300">{t('navigation')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/" className="hover:text-white transition">{t('home')}</Link></li>
                <li><Link to="/search" className="hover:text-white transition">{t('search')}</Link></li>
                {isAuthenticated && (
                  <li><Link to="/dashboard" className="hover:text-white transition">{t('myListings')}</Link></li>
                )}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-gray-300">{t('contacts')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Email: info@samuyjoy.com</li>
                <li>Телефон: +998 90 123 45 67</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-gray-400 text-xs">
            <p>&copy; 2026 Samuyjoy. {t('allRightsReserved')}</p>
          </div>
        </div>
      </footer>

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 pb-20 shadow-2xl">
            <div className="flex justify-between items-center mb-4 pb-2 border-b">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                {language === 'ru' ? 'Редактировать профиль' : 'Profilni tahrirlash'}
              </h2>
              <button
                onClick={() => setShowProfileModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 font-bold"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                  {language === 'ru' ? 'Имя' : 'Ism'}
                </label>
                <input
                  type="text"
                  className="input text-sm w-full"
                  value={profileData.first_name}
                  onChange={e => setProfileData({ ...profileData, first_name: e.target.value })}
                  placeholder={language === 'ru' ? 'Введите имя' : 'Ismingizni kiriting'}
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                  {language === 'ru' ? 'Фамилия' : 'Familiya'}
                </label>
                <input
                  type="text"
                  className="input text-sm w-full"
                  value={profileData.last_name}
                  onChange={e => setProfileData({ ...profileData, last_name: e.target.value })}
                  placeholder={language === 'ru' ? 'Введите фамилию' : 'Familiyangizni kiriting'}
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                  {language === 'ru' ? 'Телефон' : 'Telefon'}
                </label>
                <input
                  type="text"
                  className="input text-sm w-full bg-gray-100"
                  value={user?.phone || (language === 'ru' ? 'Не указан' : 'Koʻrsatilmagan')}
                  disabled
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'ru' ? 'Телефон нельзя изменить' : 'Telefon raqamini oʻzgartirish mumkin emas'}
                </p>
              </div>

              <div className="flex gap-2.5 pt-2">
                <button
                  onClick={handleProfileUpdate}
                  disabled={updating}
                  className="btn btn-primary flex-1 font-bold py-3 text-sm disabled:opacity-50"
                >
                  {updating ? (language === 'ru' ? 'Сохранение...' : 'Saqlanmoqda...') : (language === 'ru' ? 'Сохранить' : 'Saqlash')}
                </button>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="btn btn-secondary font-bold py-3 text-sm"
                >
                  {language === 'ru' ? 'Отмена' : 'Bekor qilish'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
